<template>
  <header class="app-header" :class="{ 'app-header--scrolled': isScrolled }">
    <div class="app-header__inner">
      <router-link to="/" class="app-header__logo">
        <!-- <div class="app-header__logo-emblem">
          <span>江</span>
        </div>
        <div class="app-header__logo-text">
          <span class="app-header__logo-cn">江南大学</span>
          <span class="app-header__logo-en">JIANGNAN UNIVERSITY</span>
        </div> -->
        <img src="https://lib.jiangnan.edu.cn/img/logo.png" alt="江南大学">
      </router-link>

      <nav class="app-header__nav">
        <router-link
          v-for="item in navItems"
          :key="item.path"
          :to="item.path"
          class="app-header__nav-item"
          :class="{ 'app-header__nav-item--active': isActive(item.path) }"
        >
          {{ item.label }}
        </router-link>
      </nav>

      <div class="app-header__actions">
        <button type="button" class="app-header__action-btn" @click="messageVisible = true">
          <i class="el-icon-message" />
          <span>留言</span>
        </button>
        <button
          v-if="showHeaderSearch"
          type="button"
          class="app-header__icon-btn"
          aria-label="搜索"
          @click="searchVisible = true"
        >
          <i class="el-icon-search" />
        </button>

        <el-dropdown
          v-if="isLoggedIn"
          trigger="hover"
          placement="bottom-end"
          popper-class="app-header-user-dropdown"
          @command="handleUserCommand"
        >
          <span class="app-header__user-name">
            {{ userName }}
            <i class="el-icon-arrow-down app-header__user-arrow" />
          </span>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="profile">个人中心</el-dropdown-item>
            <el-dropdown-item command="logout" divided>退出登录</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        <button
          v-else
          type="button"
          class="app-header__icon-btn"
          aria-label="用户"
          @click="loginVisible = true"
        >
          <i class="el-icon-user" />
        </button>
      </div>
    </div>

    <MessageDialog v-model="messageVisible" />
    <LoginDialog v-model="loginVisible" />
    <FullscreenSearch v-model="searchVisible" />
  </header>
</template>

<script>
import { mapGetters } from 'vuex'
import MessageDialog from '@/components/common/MessageDialog.vue'
import LoginDialog from '@/components/common/LoginDialog.vue'
import FullscreenSearch from '@/components/common/FullscreenSearch.vue'
import { isListPagePath } from '@/utils/searchNavigation'

export default {
  name: 'AppHeader',
  components: { MessageDialog, LoginDialog, FullscreenSearch },
  data() {
    return {
      isScrolled: false,
      messageVisible: false,
      loginVisible: false,
      searchVisible: false,
      navItems: [
        { label: '首页', path: '/' },
        { label: '史料知识', path: '/knowledge' },
        { label: '馆藏资源', path: '/resources' },
        { label: '主题展览', path: '/exhibition' }
      ]
    }
  },
  computed: {
    ...mapGetters(['isLoggedIn', 'userName']),
    showHeaderSearch() {
      return !isListPagePath(this.$route.path)
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    isActive(path) {
      if (path === '/') {
        return this.$route.path === '/'
      }
      return this.$route.path.startsWith(path)
    },
    handleScroll() {
      this.isScrolled = window.scrollY > 20
    },
    handleLogout() {
      this.$store.dispatch('user/logout')
      this.$message.success('已退出登录')
      if (this.$route.path === '/profile') {
        this.$router.push('/')
      }
    },
    handleUserCommand(command) {
      if (command === 'profile') {
        this.$router.push('/profile')
        return
      }
      if (command === 'logout') {
        this.handleLogout()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.app-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: $header-height;
  transition: background-color 0.3s $ease-apple, box-shadow 0.3s $ease-apple;

  &--scrolled {
    background-color: rgba($color-brown-dark, 0.95);
    box-shadow: 0 2px 20px rgba(0, 0, 0, 0.15);
  }

  &__inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    @include page-container;
    height: 100%;
  }

  &__logo {
    display: flex;
    align-items: center;
    gap: $spacing-md;
    flex-shrink: 0;

    img {
      width: 220px;
      height: 54px;
      object-fit: contain;
      transition: filter 0.3s $ease-apple;
    }
  }

  &__logo-emblem {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: $color-brown-dark;
    color: $color-white;
    font-family: $font-serif;
    font-size: rem(18);
    font-weight: 700;
  }

  &__logo-text {
    display: flex;
    flex-direction: column;
  }

  &__logo-cn {
    font-family: $font-serif;
    font-size: rem(16);
    font-weight: 600;
    color: $color-brown-dark;
    line-height: 1.2;
  }

  &__logo-en {
    font-size: rem(9);
    letter-spacing: 0.5px;
    color: $color-text-light;
  }

  &--scrolled &__logo-cn,
  &--scrolled &__logo-en {
    color: $color-white;
  }

  &--scrolled &__logo img {
    filter: none;
  }

  &__nav {
    display: flex;
    align-items: center;
    gap: $spacing-xs;
    padding: $spacing-xs;
    background: $color-brown-dark;
    border-radius: 999px;
  }

  &__nav-item {
    padding: $spacing-sm $spacing-lg;
    border-radius: 999px;
    font-size: rem(14);
    color: rgba($color-white, 0.85);
    transition: all 0.25s $ease-apple;
    white-space: nowrap;

    &:hover {
      color: $color-white;
    }

    &--active {
      background: $success-color;
      color: $color-white;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: $spacing-sm;
    padding: $spacing-xs $spacing-sm;
    background: $color-brown-dark;
    border-radius: 999px;
  }

  &__action-btn {
    display: flex;
    align-items: center;
    gap: $spacing-xs;
    padding: $spacing-sm $spacing-md;
    border-radius: 999px;
    font-size: rem(13);
    color: $color-white;
    transition: background 0.2s;

    &:hover {
      background: rgba($color-white, 0.1);
    }
  }

  &__icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    color: $color-white;
    font-size: rem(18);
    transition: background 0.2s;

    &:hover {
      background: rgba($color-white, 0.1);
    }
  }

  &__user-name {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 140px;
    padding: $spacing-sm $spacing-md;
    border-radius: 999px;
    font-size: rem(13);
    color: $color-white;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: rgba($color-white, 0.1);
    }
  }

  &__user-arrow {
    font-size: rem(12);
    flex-shrink: 0;
  }
}

@media (max-width: 960px) {
  .app-header {
    &__nav {
      display: none;
    }

    &__logo-en {
      display: none;
    }
  }
}
</style>

<style lang="scss">
.app-header-user-dropdown {
  margin-top: 4px !important;
  border: none;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);

  .el-dropdown-menu__item {
    font-size: rem(14);
    color: $color-text;

    &:hover {
      color: $success-color;
      background: rgba($success-color, 0.08);
    }
  }
}
</style>
